import { Button, Grid, Pagination, FormControl, InputLabel, Select, MenuItem, Box, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import PreviewIcon from "@mui/icons-material/Preview";
import ImageIcon from "@mui/icons-material/Image";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { ImageDeleteModalStyled, ImageListGridStyled, ImageListPaginationStyled, ImageListStyled } from "./styled";

import DashboardContent from "@/components/app/DashboardContent";
import DashboardHeader from "@/components/app/DashboardHeader";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { modalActions } from "@/context/modals";
import { MODAL_TYPES } from "@/types/modals";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import GridCard from "@/components/kit/GridCard";
import InitLoading from "@/components/app/Loading/InitLoading";
import { GRID_PER_PAGE } from "@/constants/grid-view-configs";
import { ImageFilterBody } from "@/api/types/image";
import { useDeleteImage, useGetImageListPost } from "@/api/hooks/useImage";
import { routesByEntity } from "@/constants/routes-by-entity";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";
import { usePublisherList } from "@/api/hooks/publisher";

const ImageList: React.FC = () => {
    const deleteAccess = useCheckPermissionAccess(PagesAccess.IMAGE, OperationAccess.DELETE);
    const [queryParams, setQueryParams] = useState<ImageFilterBody>({
        skip: 0,
        take: 24,
        search: "",
    });

    // Fetch publishers list for filter
    const { data: publishersData } = usePublisherList({ skip: 0, take: 1000 }, "en");

    const { mutate: deleteImage } = useDeleteImage();
    const { data, isLoading, refetch } = useGetImageListPost(queryParams);

    const handleDelete = (id: number) => {
        if (data && data.data && data.data.items) {
            const list = data?.data.items;

            const imageIndex = list.findIndex((item) => item.id === id);
            const selectedImage = list[imageIndex];

            if (selectedImage.assignedTo && selectedImage.assignedTo?.length > 0) {
                modalActions.addModal(MODAL_TYPES.CONFIRMATION_MODAL, {
                    header: "Warning: Image in use!",
                    text: (
                        <ImageDeleteModalStyled>
                            <p>
                                You are about to delete this image. However, this image is currently being used by the
                                following items. If you wish to delete it, you must first unassign the image from the
                                content.
                            </p>
                            <ul>
                                {selectedImage.assignedTo?.map((item, index) => {
                                    if (routesByEntity[item.entity.toUpperCase()](id)) {
                                        return (
                                            <li key={index}>
                                                <Link target="_blank" to={routesByEntity[item.entity.toUpperCase()](id)}>
                                                    {item.title}
                                                </Link>
                                            </li>
                                        );
                                    }

                                    return <li key={index}>Profile Avatar</li>;
                                })}
                            </ul>
                        </ImageDeleteModalStyled>
                    ),
                    handleAction: () => deleteImage(id, { onSuccess: () => refetch() }),
                    cancelButtonText: "Close",
                    disableSubmitButton: true,
                });
            } else {
                modalActions.addModal(MODAL_TYPES.CONFIRMATION_MODAL, {
                    header: "Delete Confirmation",
                    text: "Are you sure to delete this image?",
                    handleAction: () => deleteImage(id, { onSuccess: () => refetch() }),
                });
            }
        }
    };

    const handleOpen = (imageUrl: string) => {
        modalActions.addModal(MODAL_TYPES.IMAGE_PREVIEW, {
            header: "Image Preview",
            width: "832px",
            imageUrl,
        });
    };

    const handlePaginationModelChange = useCallback((page: number) => {
        setQueryParams((prev) => ({ ...prev, take: GRID_PER_PAGE, skip: (page - 1) * GRID_PER_PAGE }));
    }, []);

    const handleResetFilter = useCallback(() => {
        setQueryParams((prev) => ({
            ...prev,
            publisher: undefined,
        }));
    }, []);

    if (isLoading && !data) return <InitLoading />;

    return (
        <>
            <DashboardHeader
                title="Images"
                titleSuffix={
                    <Button
                        color="primary"
                        component={Link}
                        to={ROUTE_CONSTANTS.MEDIA_MANAGEMENT.IMAGES.ADD_NEW.ROOT.ABSOLUTE}
                        variant="contained"
                    >
                        Add New Image
                    </Button>
                }
            />
            <DashboardContent>
                <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
                    <FormControl sx={{ minWidth: 300 }}>
                        <InputLabel>Filter by Publisher</InputLabel>
                        <Select
                            value={queryParams?.publisher || ""}
                            label="Filter by Publisher"
                            onChange={(e) => {
                                const publisher = e.target.value;
                                setQueryParams((prev) => ({
                                    ...prev,
                                    publisher: publisher ? Number(publisher) : undefined,
                                }));
                            }}
                        >
                            <MenuItem value="">
                                <em>All Publishers</em>
                            </MenuItem>
                            {publishersData?.data?.items?.map((publisher) => (
                                <MenuItem key={publisher.id} value={publisher.id}>
                                    {publisher.ml_title?.en || publisher.title || `Publisher ${publisher.id}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {queryParams?.publisher && (
                        <Button
                            variant="contained"
                            onClick={handleResetFilter}
                            disabled={!queryParams?.publisher}
                            sx={{ minWidth: 120 }}
                        >
                            reset
                        </Button>
                    )}
                </Box>
                <ImageListStyled>
                    {data?.data.items && data.data.items.length > 0 ? (
                        <ImageListGridStyled loading={isLoading}>
                            <Grid container spacing={4}>
                                {data.data.items.map((item) => (
                                    <Grid key={item.id} item lg={2} md={3} xs={6}>
                                        <GridCard
                                            actions={
                                                <>
                                                    <Link
                                                        to={
                                                            ROUTE_CONSTANTS.MEDIA_MANAGEMENT.IMAGES.SHOW.SHOW_BY_ID(item.id)
                                                                .ABSOLUTE
                                                        }
                                                        style={{ color: "inherit", display: "flex", alignItems: "center" }}
                                                    >
                                                        <VisibilityIcon color="inherit" />
                                                    </Link>
                                                    <PreviewIcon color="inherit" onClick={() => handleOpen(item.url)} />
                                                    {deleteAccess ? (
                                                        <DeleteIcon color="inherit" onClick={() => handleDelete(item.id)} />
                                                    ) : null}
                                                </>
                                            }
                                            image={item.url}
                                            title={item.title.length ? item.title : item.originalName}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </ImageListGridStyled>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: "400px",
                                textAlign: "center",
                                py: 4,
                            }}
                        >
                            <ImageIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                            <Typography variant="h5" color="text.secondary" gutterBottom>
                                No Images Found
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                {queryParams?.publisher
                                    ? "No images found for the selected publisher. Try selecting a different publisher or clear the filter."
                                    : "No images have been created yet. Start by adding your first image."}
                            </Typography>
                            <Button
                                component={Link}
                                to={ROUTE_CONSTANTS.MEDIA_MANAGEMENT.IMAGES.ADD_NEW.ROOT.ABSOLUTE}
                                variant="contained"
                                color="primary"
                            >
                                Add New Image
                            </Button>
                        </Box>
                    )}
                    {data?.data.pagination && data.data.pagination?.totalItems > GRID_PER_PAGE ? (
                        <ImageListPaginationStyled>
                            <Pagination
                                count={data.data.pagination.totalPages}
                                disabled={isLoading}
                                page={data.data.pagination.currentPage}
                                onChange={(e, page) => handlePaginationModelChange(page)}
                            />
                        </ImageListPaginationStyled>
                    ) : null}
                </ImageListStyled>
            </DashboardContent>
        </>
    );
};

export default ImageList;
